import React, { useState } from "react";
import { Check, ChevronDown, ChevronUp, CornerDownRight, X } from "lucide-react";
import { MaterialTransferRecord } from "../../../../models/materialStockTransfer.model";
import {
  convertMaterialQuantity,
  getMaterialDisplayUnit,
} from "./materialTransfer.utils";

type Direction = "incoming" | "outgoing";

interface MaterialTransferCardProps {
  transfer: MaterialTransferRecord;
  direction: Direction;
  isAdmin: boolean;
  userId: string;
  actionLoading: boolean;
  onAccept: (id: string, remark: string) => Promise<boolean>;
  onCancel: (id: string, remark: string) => Promise<boolean>;
}

const getName = (
  value: string | { _id: string; name?: string; username?: string } | undefined,
) => (typeof value === "string" ? "—" : value?.name || value?.username || "—");

const getId = (
  value: string | { _id: string } | undefined,
): string | undefined => (typeof value === "string" ? value : value?._id);

const MaterialTransferCard: React.FC<MaterialTransferCardProps> = ({
  transfer,
  direction,
  isAdmin,
  userId,
  actionLoading,
  onAccept,
  onCancel,
}) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [remark, setRemark] = useState("");
  const isIncoming = direction === "incoming";
  const material =
    typeof transfer.materialId === "string" ? null : transfer.materialId;
  const baseUnit = material?.unit || "";
  const displayUnit = baseUnit ? getMaterialDisplayUnit(baseUnit) : baseUnit;
  const displayQuantity = baseUnit
    ? (convertMaterialQuantity(transfer.quantity, baseUnit, displayUnit) ??
      transfer.quantity)
    : transfer.quantity;
  const canCancel = isAdmin || (userId && getId(transfer.sentBy) === userId);

  const handleSubmit = async () => {
    const success = await (isIncoming
      ? onAccept(transfer._id, remark)
      : onCancel(transfer._id, remark));
    if (success) setIsConfirming(false);
  };

  const renderAction = () =>
    transfer.status === "IN_TRANSIT" && (isIncoming || canCancel) ? (
      !isConfirming ? (
        <button
          type="button"
          disabled={actionLoading}
          onClick={() => {
            setIsConfirming(true);
            setRemark("");
          }}
          className={`inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold text-white transition disabled:opacity-50 ${
            isIncoming
              ? "bg-green-700 hover:bg-green-800"
              : "bg-red-700 hover:bg-red-800"
          }`}
        >
          {isIncoming ? <CornerDownRight size={17} /> : <X size={17} />}
          {isIncoming ? "Accept" : "Cancel"}
        </button>
      ) : (
        <div className="flex flex-col gap-2">
          <input
            value={remark}
            onChange={(event) => setRemark(event.target.value)}
            disabled={actionLoading}
            placeholder={
              isIncoming
                ? "Receiver remark (optional)"
                : "Cancellation reason (optional)"
            }
            className="min-h-10 min-w-0 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0077C0] focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={actionLoading}
              className={`inline-flex min-h-10 flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
                isIncoming
                  ? "bg-green-700 hover:bg-green-800"
                  : "bg-red-700 hover:bg-red-800"
              }`}
            >
              {actionLoading ? (
                <span className="animate-pulse">…</span>
              ) : (
                <Check size={16} />
              )}
              Confirm
            </button>
            <button
              type="button"
              aria-label="Cancel action"
              disabled={actionLoading}
              onClick={() => {
                setIsConfirming(false);
                setRemark("");
              }}
              className="min-h-10 rounded-lg border border-gray-300 px-3 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )
    ) : (
      <span className="text-sm text-gray-400">No action available</span>
    );

  return (
    <>
      <tr className="hidden border-b border-gray-100 align-top transition-colors hover:bg-gray-50 lg:table-row">
        <td className="px-4 py-4">
          <p className="font-semibold text-gray-900">
            {material?.name || "Material"}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            SKU: {material?.sku || "—"}
          </p>
        </td>
        <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-gray-800">
          {displayQuantity.toLocaleString(undefined, {
            maximumFractionDigits: 4,
          })}{" "}
          {displayUnit}
        </td>
        <td className="px-4 py-4 text-sm text-gray-700">
          <p className="font-medium text-gray-900">
            {getName(transfer.fromWarehouse)}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            To {getName(transfer.toWarehouse)}
          </p>
        </td>
        <td className="px-4 py-4 text-sm">
          <p className="font-medium text-gray-800">
            {getName(transfer.sentBy)}
          </p>
          <p className="mt-1 whitespace-nowrap text-xs text-gray-500">
            {new Date(transfer.sentDate).toLocaleString()}
          </p>
        </td>
        <td className="whitespace-nowrap px-4 py-4">
          <span
            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
              transfer.status === "IN_TRANSIT"
                ? "bg-amber-50 text-amber-800"
                : "bg-gray-100 text-gray-700"
            }`}
          >
            {transfer.status.replace(/_/g, " ")}
          </span>
        </td>
        <td className="min-w-[190px] px-4 py-4">{renderAction()}</td>
        <td className="whitespace-nowrap px-4 py-4">
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-controls={`material-transfer-details-${transfer._id}`}
            onClick={() => setIsExpanded((expanded) => !expanded)}
            className="inline-flex min-h-10 items-center gap-1 rounded-lg border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {isExpanded ? "Hide details" : "Details"}
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </td>
      </tr>
      {isExpanded && (
        <tr
          id={`material-transfer-details-${transfer._id}`}
          className="hidden bg-gray-50 lg:table-row"
        >
          <td colSpan={7} className="px-5 py-4">
            <div className="grid gap-4 text-sm md:grid-cols-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Serial numbers
                </p>
                <p className="mt-1 break-words font-mono text-xs text-gray-700">
                  {transfer.serialNumbers?.length
                    ? transfer.serialNumbers.join(", ")
                    : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Sender remark
                </p>
                <p className="mt-1 break-words text-gray-700">
                  {transfer.senderRemark || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Receiver remark
                </p>
                <p className="mt-1 break-words text-gray-700">
                  {transfer.receiverRemark || "—"}
                </p>
              </div>
            </div>
          </td>
        </tr>
      )}
      <tr className="border-b border-gray-100 lg:hidden">
        <td colSpan={7} className="p-3">
          <article className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="break-words font-semibold text-gray-900">
                  {material?.name || "Material"}
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  SKU: {material?.sku || "—"}
                </p>
              </div>
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                  transfer.status === "IN_TRANSIT"
                    ? "bg-amber-50 text-amber-800"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {transfer.status.replace(/_/g, " ")}
              </span>
            </div>

            <div className="my-4 grid grid-cols-2 gap-x-3 gap-y-4 border-y border-gray-100 py-4 text-sm">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  Quantity
                </p>
                <p className="mt-1 font-semibold text-gray-800">
                  {displayQuantity.toLocaleString(undefined, {
                    maximumFractionDigits: 4,
                  })}{" "}
                  {displayUnit}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  Sent by
                </p>
                <p className="mt-1 text-gray-700">{getName(transfer.sentBy)}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  Warehouse route
                </p>
                <p className="mt-1 break-words font-medium text-gray-800">
                  {getName(transfer.fromWarehouse)}
                  <span className="mx-2 text-gray-400">→</span>
                  {getName(transfer.toWarehouse)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  Sent date
                </p>
                <p className="mt-1 text-gray-700">
                  {new Date(transfer.sentDate).toLocaleString()}
                </p>
              </div>
              {transfer.receivedDate && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                    Received date
                  </p>
                  <p className="mt-1 text-gray-700">
                    {new Date(transfer.receivedDate).toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            <details className="mb-4 rounded-lg border border-gray-200">
              <summary className="flex min-h-11 cursor-pointer items-center justify-between px-3 text-sm font-medium text-gray-700">
                Serial numbers and remarks
                <ChevronDown size={16} />
              </summary>
              <div className="grid gap-3 border-t border-gray-100 p-3 text-sm">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Serial numbers
                  </p>
                  <p className="mt-1 break-words font-mono text-xs text-gray-700">
                    {transfer.serialNumbers?.length
                      ? transfer.serialNumbers.join(", ")
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Sender remark
                  </p>
                  <p className="mt-1 break-words text-gray-700">
                    {transfer.senderRemark || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Receiver remark
                  </p>
                  <p className="mt-1 break-words text-gray-700">
                    {transfer.receiverRemark || "—"}
                  </p>
                </div>
              </div>
            </details>

            <div className="flex flex-col gap-2">{renderAction()}</div>
          </article>
        </td>
      </tr>
    </>
  );
};

export default MaterialTransferCard;
